import { IconRemove } from '@newfront-insurance/core-ui';
import { Button, Skeleton } from '@newfront-insurance/core-ui/v2';

import type { TeamSkillRow } from './team-detail-model';
import { useTeamSkills } from './use-team-skills';
import type { SettingsTeamDetails } from '../../../../queries/skill-administration';
import { SettingsSectionList, SettingsSectionListRow } from '../settings-section-list';
import { SettingsActionButton } from '../settings-shared/settings-action-button';

export function TeamSkillsList({
  group,
  onRevoke,
  disabled,
  pendingKey,
}: {
  group: SettingsTeamDetails;
  onRevoke: (skill: TeamSkillRow) => void;
  disabled: boolean;
  pendingKey: string | null;
}) {
  const { skills, loading, failed, retry } = useTeamSkills(group);

  return (
    <SettingsSectionList
      title="Skills shared to this team"
      loading={loading}
      emptyMessage={!skills.length ? 'No skills have been shared to this team yet.' : undefined}
      headerEnd={
        <span className="text-steel-400 text-sm leading-4">
          {skills.length} {skills.length === 1 ? 'skill' : 'skills'}
        </span>
      }
    >
      {failed ? (
        <li className="text-steel-400 px-3.5 py-4 text-sm">
          <div role="alert">
            Couldn't load skills.{' '}
            <Button type="button" variant="inline" className="text-sm" onClick={retry}>
              Retry
            </Button>
          </div>
        </li>
      ) : null}
      {skills.map((skill, index) => {
        if (skill.kind === 'custom' && failed) return null;
        if (skill.kind === 'custom' && loading)
          return (
            <SettingsSectionListRow
              key={skill.key}
              loadingLabel={`Loading skill ${index + 1}`}
              trailing={<Skeleton aria-hidden="true" className="bg-steel-100 h-4 w-36 max-w-full" />}
            />
          );
        return (
          <SettingsSectionListRow
            key={skill.key}
            title={skill.title}
            description={skill.description}
            trailing={
              <div className="flex max-w-full items-center gap-3">
                <span className="text-steel-400 min-w-0 text-sm leading-4 break-words">{skill.attribution}</span>
                <SettingsActionButton
                  label={`Revoke ${skill.title} from ${group.name}`}
                  tooltip="Revoke skill from team"
                  disabled={disabled}
                  ariaBusy={pendingKey === skill.key}
                  onClick={() => onRevoke(skill)}
                >
                  <span aria-hidden="true">
                    <IconRemove color="currentColor" size={16} />
                  </span>
                </SettingsActionButton>
              </div>
            }
          />
        );
      })}
    </SettingsSectionList>
  );
}
